import clsx from 'clsx';
import { isAfter, isBefore, isSameDay, isSameMonth, parseISO } from 'date-fns';
import { assoc } from 'ramda';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { DateValue } from 'react-aria';
import { useLocation, useParams } from 'react-router-dom';
import Button from 'src/components/Button';
import CenteredLoader from 'src/components/CenteredLoader';
import ErrorMessage from 'src/components/ErrorMessage';
import ExtraLabel from 'src/components/ExtraLabel';
import {
  Sidebar,
  SidebarContainer,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTitle,
} from 'src/components/Sidebar';
import Tag from 'src/components/Tag';
import { Tooltip } from 'src/components/Tooltip';
import { useLocationQuery } from 'src/dictionaries/dictionariesApi';
import { useBreakpoint } from 'src/hooks/useBreakpoint';
import useSelectedStudent from 'src/hooks/useSelectedStudent';
import useTimeFormat from 'src/hooks/useTimeFormat';
import { IconCheck } from 'src/icons';
import { isParaServiceType, sameServiceType } from 'src/logic/serviceType';
import { useInvoiceDetailsQuery } from 'src/pages/billing/billingApi';
import { useSingleStudentDetailsQuery } from 'src/pages/single-student/singleStudentApi';
import { Student } from 'src/pages/students/students.types';
import { isFutureDate } from 'src/utils/isFutureDate';
import { keyValueToSelect } from 'src/utils/keyValueToSelect';

import { ScheduleEvent } from '../schedule.types';
import { useLazyGetRecordSessionQuery, useStudentDisabledDaysQuery } from '../scheduleApi';
import CancelSessionModal, { useCancelSession } from './CancelSessionModal';
import { isConfirmBlockedByMissingGoals } from './confirmSessionGoals';
import DeleteSessionAlertDialog, { useDeleteSession } from './DeleteSessionAlertDialog';
import { RecordSessionContextProvider } from './RecordSessionContext';
import SessionFormPart from './SessionFormPart';
import StudentForm from './StudentForm';
import { useRecordSession } from './useRecordSession';

export type RecordSessionSidebarRefProps = {
  openWithSelectedDate: (selectedDate: Date) => void;
  openWithSelectedSession: (event: ScheduleEvent, options?: { limitDatePicker?: boolean }) => void;
};

type RecordSessionFormProps = {
  lockOnStudentId?: string;
  onSuccessCb?: () => void;
};

type RecordSessionFormBaseProps = {
  onSuccessCb: RecordSessionFormProps['onSuccessCb'];
};

const RecordSessionFormBase = forwardRef<RecordSessionSidebarRefProps, RecordSessionFormBaseProps>(
  function RecordSessionForm({ onSuccessCb }, ref) {
    const { data, dispatch, actions, isEdit, features } = useRecordSession({
      onSuccessCb,
    });
    const {
      state,
      formData: originalFormData,
      isLoading,
      errors: errorsTmp,
      isLoadingConfirm,
      isLoadingCreate,
      isLoadingReschedule,
      options,
      requiredGoals,
    } = data;
    const { confirmSession, rescheduleSession } = actions;
    const { id: invoiceId } = useParams();
    const location = useLocation();
    const isStudentProfile = location.pathname.includes('student');
    const { data: invoiceData } = useInvoiceDetailsQuery(invoiceId!, {
      skip: !invoiceId || isStudentProfile,
    });
    const {
      refetch,
      isUninitialized,
      isLoading: isLoadingStudentDetails,
    } = useSingleStudentDetailsQuery(
      invoiceData ? invoiceData.studentId : originalFormData?.studentId || '',
      {
        skip: !invoiceData?.studentId && !originalFormData?.studentId,
      },
    );

    const studentIdForDisabledDaysQuery = originalFormData?.studentId || '';
    const { data: disabledDays } = useStudentDisabledDaysQuery(studentIdForDisabledDaysQuery, {
      skip: !originalFormData?.studentId,
    });

    const {
      session: sessionData,
      onCancelSession,
      onAbortSessionCancellation,
    } = useCancelSession();

    const [getRecordSession] = useLazyGetRecordSessionQuery();

    const { data: locations = [] } = useLocationQuery();
    const [isEditingConfirmedSession, setIsEditingConfirmedSession] = useState(false);
    const [isReschedulingCancelledSession, setIsReschedulingCancelledSession] = useState(false);
    const isSidebarOpen = state.isInitialized;
    const existingSession = 'existingSession' in state ? state.existingSession : undefined;
    const originDate = existingSession?.originDate || existingSession?.date;
    const isConfirmed = existingSession?.isConfirmed;
    const canBeDeleted = existingSession?.canBeDeleted;
    const isEditable = existingSession?.isEditable;
    const isCancelled = existingSession?.isCancelled;
    const isRescheduled = existingSession?.isRescheduled;
    const canReschedule = existingSession?.canReschedule !== false;
    const existingSessionId = existingSession?.id;
    const serviceType = existingSession?.student?.serviceType;
    const isSeitServiceType = sameServiceType(serviceType, 'SEIT');
    const isSetssServiceType = sameServiceType(serviceType, 'SETSS');
    const isFadSession = existingSession?.isFadSession;
    const { format } = useTimeFormat();
    const { isSm } = useBreakpoint('sm');
    const { selectedStudent } = useSelectedStudent(
      true,
      originalFormData?.studentId,
      existingSession?.student,
    );
    // Prefer the API flag; fall back to the raw service type so an older payload cannot re-enable the goals gate.
    const isPara =
      selectedStudent?.isPara === true ||
      isParaServiceType(selectedStudent?.serviceType) ||
      isParaServiceType(existingSession?.student?.serviceType);

    const processedFormData = useMemo(() => {
      if (originalFormData && Array.isArray(originalFormData.goals)) {
        return originalFormData;
      }
      return {
        ...originalFormData,
        goals: [],
      } as import('./RecordSessionContext').FormData;
    }, [originalFormData]);

    const formDisabled = isConfirmed && !isEditingConfirmedSession;

    const locationsOptions = useMemo(
      () =>
        keyValueToSelect(
          locations.filter(
            (l) =>
              data.availability?.canSchedule &&
              data.availability.parameters.locations.includes(l.value),
          ),
        ),
      [data.availability, locations],
    );

    useEffect(() => {
      if (!isSidebarOpen) {
        setIsEditingConfirmedSession(false);
        setIsReschedulingCancelledSession(false);
      }
    }, [isSidebarOpen]);

    useEffect(() => {
      if (isCancelled && isSeitServiceType) {
        setIsReschedulingCancelledSession(true);
      }
    }, [isCancelled, isSeitServiceType]);

    useEffect(() => {
      if (selectedStudent?.groupSize && !isEdit) {
        dispatch({ type: 'numberOfStudents', payload: 1 });
      }
    }, [selectedStudent?.groupSize, dispatch, isEdit]);

    const {
      onDeleteSession,
      onCancelDeleteSession,
      sessionId: sessionIdToDelete,
    } = useDeleteSession();

    const closeForm = useCallback(() => {
      dispatch({ type: 'reset' });
    }, [dispatch]);

    useImperativeHandle(
      ref,
      () => ({
        openWithSelectedDate: (selectedDate: Date) => {
          const startTime = format(selectedDate, 'HH:mm');
          dispatch({
            type: 'init',
            payload: {
              parentDate: selectedDate,
              formData: {
                date: selectedDate.toISOString(),
                goals: [],
                startTime: startTime !== '00:00' ? startTime : undefined,
              },
            },
          });
        },
        openWithSelectedSession: async (event, options) => {
          try {
            dispatch({
              type: 'init',
              payload: { isLoading: true, parentEvent: event, formData: { goals: [] }, options },
            });
            dispatch({ type: 'loadingDone', payload: await getRecordSession(event.id).unwrap() });
          } catch (err) {
            dispatch({ type: 'reset' });
            console.error(err);
          }
        },
      }),
      [getRecordSession, dispatch, format],
    );

    const handleConfirmSession = () => {
      confirmSession({});
      if (!isUninitialized) {
        refetch();
      }
    };

    const handleCancelSession = () => {
      if ('existingSession' in state && state.existingSession?.id) {
        onCancelSession({
          sessionId: state.existingSession.id ?? '',
          date: processedFormData.date ?? '',
          startTime: processedFormData.startTime ?? '',
          location: processedFormData.location ?? '',
          finishTime: processedFormData.finishTime ?? '',
        });
        if (!isUninitialized) {
          refetch();
        }
      }
    };

    const handleStudentChange = useCallback(
      (student: Student) => {
        dispatch({ type: 'selectStudent', payload: student.id });
      },
      [dispatch],
    );

    const isDateDisabled = useCallback(
      (d: DateValue) => {
        const dDate = new Date(d.year, d.month - 1, d.day);
        const isLimitedToMonthAndIsNotThisMonth =
          !options.limitDatePicker || !originDate
            ? false
            : !isSameMonth(parseISO(originDate), dDate);

        const isReschedulingAndIsBeforeOriginDate =
          isReschedulingCancelledSession &&
          originDate &&
          !(isSameDay(dDate, parseISO(originDate)) || isAfter(dDate, parseISO(originDate)));

        const isOutOfPlacementRange =
          originalFormData?.studentId && disabledDays?.startDate && disabledDays.finishDate
            ? isBefore(dDate, parseISO(disabledDays.startDate)) ||
              isAfter(dDate, parseISO(disabledDays.finishDate))
            : false;

        const isUnavailableDate =
          originalFormData?.studentId && disabledDays?.unavailableDates.length
            ? disabledDays?.unavailableDates.some((unavailableDay) =>
                isSameDay(dDate, parseISO(unavailableDay)),
              )
            : false;

        return (
          isLimitedToMonthAndIsNotThisMonth ||
          !!isReschedulingAndIsBeforeOriginDate ||
          isOutOfPlacementRange ||
          isUnavailableDate
        );
      },
      [
        options.limitDatePicker,
        originDate,
        isReschedulingCancelledSession,
        disabledDays,
        originalFormData?.studentId,
      ],
    );

    const selectedDateIsInFuture = useMemo(
      () => isFutureDate(processedFormData.date),
      [processedFormData.date],
    );

    const isDateOrTimeMissing =
      !processedFormData.date || !processedFormData.startTime || !processedFormData.finishTime;

    const hasDateChanged = useMemo(() => {
      if (!processedFormData.date) {
        return false;
      }
      return processedFormData.date !== existingSession?.date;
    }, [processedFormData.date, existingSession?.date]);

    const shouldReschedule =
      isSeitServiceType && (isRescheduled || isReschedulingCancelledSession) && hasDateChanged;
    const cancelledActionLabel = shouldReschedule ? 'Reschedule Session' : 'Confirm Session';
    const cancelledActionMissingDateText = shouldReschedule
      ? 'You have to select the date and time to be able to reschedule the session'
      : 'You have to select the date and time to be able to confirm the session';

    const errors =
      selectedDateIsInFuture && !isReschedulingCancelledSession
        ? assoc('date', 'You cannot confirm sessions for future days', errorsTmp)
        : errorsTmp;

    const errorReschedule =
      !canReschedule &&
      (isReschedulingCancelledSession || isRescheduled) &&
      isParaServiceType(serviceType)
        ? 'PARA sessions cannot be rescheduled'
        : '';

    const isSeitGoal = processedFormData.goals.some((goal) => goal.type === 'seit');

    const isInvalidSeitCondition = useMemo(() => {
      if (!isSeitGoal) return false;
      const notEnoughGoals = requiredGoals > processedFormData.goals.length;
      const incompleteGoals = processedFormData.goals.some(
        (goal) =>
          goal.type === 'seit' && (!goal.activity?.length || !goal.participation || !goal.outcome),
      );
      return notEnoughGoals || incompleteGoals;
    }, [isSeitGoal, requiredGoals, processedFormData.goals]);

    const hideConfirmTooltip = useMemo(() => {
      const areIncompleteGoals = processedFormData.goals.some(
        (goal) =>
          goal.type === 'seit' && (!goal.activity?.length || !goal.participation || !goal.outcome),
      );
      if (
        isConfirmBlockedByMissingGoals({
          goalsCount: processedFormData.goals.length,
          isPara,
        }) ||
        !processedFormData.location ||
        !processedFormData.startTime ||
        !processedFormData.finishTime ||
        !processedFormData.date ||
        areIncompleteGoals
      ) {
        return true;
      }
      return false;
    }, [processedFormData, isPara]);

    const isMissingSetssGoalsCondition = useMemo(() => {
      if (isSeitGoal) return false;
      return processedFormData.goals?.length === 0 && !isPara;
    }, [isSeitGoal, processedFormData.goals, isPara]);

    const confirmSessionValidationTooltipText = useMemo(() => {
      if (selectedDateIsInFuture) return 'You cannot confirm session in the future';
      if (hideConfirmTooltip) return '';

      if (isSeitGoal) {
        if (isInvalidSeitCondition) {
          return `${requiredGoals} goal(s) required based on session length.`;
        }
      } else {
        if (isMissingSetssGoalsCondition)
          return `Session cannot be confirmed without goal progress.`;
      }
      return undefined;
    }, [
      selectedDateIsInFuture,
      isSeitGoal,
      isInvalidSeitCondition,
      requiredGoals,
      isMissingSetssGoalsCondition,
      hideConfirmTooltip,
    ]);

    const isConfirmButtonDisabled = useMemo(() => {
      if (selectedDateIsInFuture) return true;
      if (
        !processedFormData.date ||
        !processedFormData.startTime ||
        !processedFormData.finishTime
      ) {
        return true;
      }

      if (
        isConfirmBlockedByMissingGoals({
          goalsCount: processedFormData.goals.length,
          isPara,
        })
      ) {
        return true;
      }

      if (isSeitGoal) {
        return isInvalidSeitCondition;
      }
      return isMissingSetssGoalsCondition;
    }, [
      selectedDateIsInFuture,
      isPara,
      processedFormData.goals,
      processedFormData.date,
      processedFormData.startTime,
      processedFormData.finishTime,
      isSeitGoal,
      isInvalidSeitCondition,
      isMissingSetssGoalsCondition,
    ]);

    const name = useMemo(() => {
      if (isEdit) {
        if (isLoading) {
          return '';
        }
        if (isCancelled) {
          return 'Reschedule session';
        }
        if (isConfirmed) {
          return 'Edit Session';
        }
        return 'Confirm session';
      }
      return 'Add session';
    }, [isEdit, isCancelled, isLoading, isConfirmed]);

    const makeupSessionLabel = useMemo(() => {
      if (isLoading) {
        return '';
      }
      if (isSeitServiceType) {
        return 'Makeup session';
      }
      return '';
    }, [isLoading, isSeitServiceType]);

    return (
      <>
        <SidebarContainer
          open={isSidebarOpen}
          onOpenChange={(open) => !open && dispatch({ type: 'reset' })}
        >
          <Sidebar open={isSidebarOpen}>
            <SidebarHeader className="w-full">
              <div className="flex w-full items-center justify-between">
                <SidebarTitle>{name}</SidebarTitle>
                {isFadSession && name === 'Edit Session' && (
                  <Tag preset="lightBlue" size="md">
                    FAD
                  </Tag>
                )}
              </div>
            </SidebarHeader>
            <SidebarContent className="flex flex-col gap-8">
              {isLoading ? (
                <CenteredLoader />
              ) : (
                <>
                  <StudentForm
                    isStudentLocked={!!isEdit || features.isStudentLocked}
                    onStudentSelect={handleStudentChange}
                    onStudentClear={() => dispatch({ type: 'clearStudent' })}
                    selectedStudentId={processedFormData.studentId}
                    savedStudent={existingSession?.student}
                    error={errors?.studentId}
                    disabled={formDisabled}
                  />

                  <SessionFormPart
                    dispatch={dispatch}
                    studentId={processedFormData.studentId}
                    savedStudent={existingSession?.student}
                    isPara={isPara}
                    locationsOptions={locationsOptions}
                    goals={processedFormData.goals}
                    disabled={isCancelled || isRescheduled || formDisabled}
                    date={
                      isCancelled || isRescheduled
                        ? existingSession?.originDate || existingSession?.date
                        : processedFormData.date
                    }
                    availability={data.availability}
                    startTime={
                      isCancelled || isRescheduled
                        ? existingSession?.originStartTime || existingSession?.startTime
                        : processedFormData.startTime
                    }
                    finishTime={
                      isCancelled || isRescheduled
                        ? existingSession?.originFinishTime || existingSession?.finishTime
                        : processedFormData.finishTime
                    }
                    location={
                      isCancelled || isRescheduled
                        ? existingSession?.originLocation || existingSession?.location
                        : processedFormData.location
                    }
                    errors={isCancelled || isRescheduled || isRescheduled ? undefined : errors}
                    isDateDisabled={isDateDisabled}
                    note={isCancelled || isRescheduled ? undefined : processedFormData.note}
                    cancellationReason={
                      isCancelled || isRescheduled
                        ? existingSession?.cancellationReason
                        : processedFormData.cancellationReason
                    }
                    cancellationReasonId={
                      isCancelled || isRescheduled
                        ? existingSession?.cancellationReasonId
                        : processedFormData.cancellationReasonId
                    }
                    numberOfStudents={
                      isCancelled || isRescheduled
                        ? existingSession?.numberOfStudents
                        : processedFormData.numberOfStudents
                    }
                    requiredGoals={requiredGoals}
                  />

                  {(isRescheduled || (isReschedulingCancelledSession && isEditable)) && (
                    <div className="-mx-6 border-t border-t-neutral-800 px-6 pt-6">
                      <ExtraLabel className="mb-6">{makeupSessionLabel}</ExtraLabel>
                      <SessionFormPart
                        dispatch={dispatch}
                        studentId={processedFormData.studentId}
                        savedStudent={existingSession?.student}
                        isPara={isPara}
                        locationsOptions={locationsOptions}
                        goals={processedFormData.goals}
                        disabled={formDisabled || !isEditable}
                        date={processedFormData.date}
                        availability={data.availability}
                        startTime={processedFormData.startTime}
                        finishTime={processedFormData.finishTime}
                        location={processedFormData.location}
                        errors={errors}
                        isDateDisabled={isDateDisabled}
                        note={processedFormData.note}
                        numberOfStudents={processedFormData.numberOfStudents}
                        requiredGoals={requiredGoals}
                      />
                    </div>
                  )}
                  {(isConfirmed || isCancelled) && !isEditable && (
                    <ErrorMessage>{`Finalized sessions cannot be edited.`}</ErrorMessage>
                  )}
                </>
              )}
            </SidebarContent>
            {existingSessionId && isEditable && !isSm && (
              <div className="px-6 py-4">
                <Tooltip
                  side="bottom"
                  content={isFadSession ? 'FAD session cannot be canceled or deleted' : ''}
                  delayDuration={0}
                >
                  <Button
                    preset="warning"
                    size="sm"
                    className={clsx(!processedFormData.cancellationReasonId && 'w-full')}
                    onClick={onDeleteSession.bind(null, existingSessionId)}
                    disabled={isFadSession}
                  >
                    Delete
                  </Button>
                </Tooltip>
              </div>
            )}
            <SidebarFooter error={errors?._ || errorReschedule}>
              {isConfirmed && !isEditingConfirmedSession ? (
                <>
                  <Button preset="quaternary" disabled Icon={IconCheck}>
                    Session confirmed
                  </Button>
                  {isEditable && (
                    <Button preset="tertiary" onClick={() => setIsEditingConfirmedSession(true)}>
                      Edit Session
                    </Button>
                  )}
                </>
              ) : isCancelled ? (
                isReschedulingCancelledSession || isRescheduled ? (
                  <>
                    {isDateOrTimeMissing ? (
                      <Tooltip content={cancelledActionMissingDateText}>
                        <Button
                          disabled={isDateOrTimeMissing || !!errorReschedule}
                          preset="secondary"
                        >
                          {cancelledActionLabel}
                        </Button>
                      </Tooltip>
                    ) : shouldReschedule ? (
                      <Button
                        disabled={isDateOrTimeMissing || !!errorReschedule}
                        preset="secondary"
                        isLoading={isLoadingReschedule}
                        onClick={rescheduleSession}
                      >
                        Reschedule Session
                      </Button>
                    ) : (
                      <Tooltip
                        side="bottom"
                        content={confirmSessionValidationTooltipText}
                        delayDuration={0}
                        className="w-72"
                      >
                        <Button
                          preset="secondary"
                          isLoading={isLoadingReschedule}
                          onClick={handleConfirmSession}
                          disabled={
                            isDateOrTimeMissing || isConfirmButtonDisabled || !!errorReschedule
                          }
                        >
                          Confirm Session
                        </Button>
                      </Tooltip>
                    )}
                  </>
                ) : (
                  <Button
                    preset="secondary"
                    isLoading={isLoadingReschedule}
                    onClick={() => setIsReschedulingCancelledSession(true)}
                    disabled={!isEditable}
                  >
                    Reschedule Session
                  </Button>
                )
              ) : (
                <div className="grid w-full grid-cols-2 gap-4">
                  {shouldReschedule ? (
                    <Button
                      preset="secondary"
                      onClick={rescheduleSession}
                      isLoading={isLoadingReschedule}
                      disabled={isDateOrTimeMissing || !!errorReschedule}
                    >
                      Reschedule Session
                    </Button>
                  ) : (
                    <Tooltip
                      side="bottom"
                      content={confirmSessionValidationTooltipText}
                      delayDuration={0}
                    >
                      <Button
                        preset="secondary"
                        onClick={handleConfirmSession}
                        isLoading={isLoadingConfirm || isLoadingCreate}
                        disabled={isDateOrTimeMissing || isConfirmButtonDisabled}
                      >
                        Confirm Session
                      </Button>
                    </Tooltip>
                  )}

                  {isEdit && !isLoading && !isRescheduled && !isFadSession && (
                    <Button preset="tertiary" onClick={handleCancelSession}>
                      Cancel Session
                    </Button>
                  )}
                </div>
              )}

              {existingSessionId && isEditable && isSm && (
                <Tooltip
                  side="bottom"
                  content={
                    !canBeDeleted
                      ? 'Scheduled sessions cannot be deleted; cancel and/or reschedule as needed.'
                      : isFadSession
                      ? 'FAD session cannot be canceled or deleted'
                      : ''
                  }
                  delayDuration={0}
                >
                  <Button
                    preset="warning"
                    className={clsx(!processedFormData.cancellationReasonId && 'ml-auto')}
                    onClick={onDeleteSession.bind(null, existingSessionId)}
                    disabled={!canBeDeleted || isLoadingStudentDetails || isFadSession}
                  >
                    Delete
                  </Button>
                </Tooltip>
              )}
            </SidebarFooter>
          </Sidebar>
        </SidebarContainer>
        <CancelSessionModal
          onAbort={onAbortSessionCancellation}
          onSuccessCb={closeForm}
          cancelSessionData={sessionData}
        />

        <DeleteSessionAlertDialog
          sessionId={sessionIdToDelete}
          onCancel={onCancelDeleteSession}
          onSuccessCb={closeForm}
        />
      </>
    );
  },
);

const RecordSessionForm = forwardRef<RecordSessionSidebarRefProps, RecordSessionFormProps>(
  ({ onSuccessCb, ...props }, ref) => {
    return (
      <RecordSessionContextProvider {...props}>
        <RecordSessionFormBase ref={ref} onSuccessCb={onSuccessCb}></RecordSessionFormBase>
      </RecordSessionContextProvider>
    );
  },
);
RecordSessionForm.displayName = 'RecordSessionFormWrapper';

export default RecordSessionForm;
